<template>
  <div class="reset-password">
    <div
      class="min-h-screen flex items-center justify-center bg-background py-12 px-4 sm:px-6 lg:px-8"
    >
      <div class="max-w-md w-full space-y-8 bg-card rounded-xl border border-border shadow-sm p-8">
        <div>
          <h2 class="mt-6 text-center text-3xl font-extrabold text-foreground">
            {{ $t('auth.setNewPasswordTitle') }}
          </h2>
          <p class="mt-2 text-center text-sm text-muted-foreground">
            {{ $t('auth.enterNewPasswordBelow') }}
          </p>
        </div>

        <AlertBanner v-if="success" variant="success" class="mt-8">
          <p class="text-sm">
            {{ successMessage }}
            <router-link
              :to="localePath('Login')"
              class="font-medium text-primary hover:text-primary/80"
            >
              {{ $t('auth.signInNow') }}
            </router-link>
          </p>
        </AlertBanner>

        <form v-else class="mt-8 space-y-6" novalidate @submit.prevent="handleSubmit">
          <AlertBanner v-if="error" variant="error">
            <p class="text-sm">{{ error }}</p>
          </AlertBanner>

          <div class="space-y-4">
            <div>
              <label for="password" class="block text-sm font-medium text-foreground mb-1">{{
                $t('auth.newPasswordLabel')
              }}</label>
              <input
                id="password"
                v-model="password"
                type="password"
                :class="[
                  'appearance-none relative block w-full px-3 py-2 border placeholder-muted-foreground text-foreground rounded-md focus:outline-none sm:text-sm',
                  fieldErrors.password
                    ? 'border-destructive focus:ring-destructive focus:border-destructive'
                    : 'border-input bg-input-background focus:ring-ring focus:border-ring',
                ]"
                :placeholder="$t('auth.passwordMinLength')"
              />
              <p v-if="fieldErrors.password" class="mt-1 text-sm text-destructive">
                {{ fieldErrors.password }}
              </p>
            </div>
            <div>
              <label
                for="password-confirmation"
                class="block text-sm font-medium text-foreground mb-1"
                >{{ $t('auth.confirmNewPasswordLabel') }}</label
              >
              <input
                id="password-confirmation"
                v-model="passwordConfirmation"
                type="password"
                :class="[
                  'appearance-none relative block w-full px-3 py-2 border placeholder-muted-foreground text-foreground rounded-md focus:outline-none sm:text-sm',
                  fieldErrors.passwordConfirmation
                    ? 'border-destructive focus:ring-destructive focus:border-destructive'
                    : 'border-input bg-input-background focus:ring-ring focus:border-ring',
                ]"
                :placeholder="$t('auth.confirmNewPasswordPlaceholder')"
              />
              <p v-if="fieldErrors.passwordConfirmation" class="mt-1 text-sm text-destructive">
                {{ fieldErrors.passwordConfirmation }}
              </p>
            </div>
          </div>

          <div>
            <button
              type="submit"
              :disabled="loading"
              class="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-primary-foreground bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ring disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <LoadingSpinner :loading="loading" />
              {{ $t('auth.resetPassword') }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useLocalePath } from '../../composables/useLocalePath'
import apiClient from '../../api/client'
import LoadingSpinner from '../../components/LoadingSpinner.vue'
import AlertBanner from '../../components/AlertBanner.vue'

const route = useRoute()
const { t } = useI18n()
const { localePath } = useLocalePath()

const password = ref('')
const passwordConfirmation = ref('')
const loading = ref(false)
const error = ref(null)
const success = ref(false)
const successMessage = ref('')
const fieldErrors = reactive({})

const validate = () => {
  delete fieldErrors.password
  delete fieldErrors.passwordConfirmation

  if (!password.value) {
    fieldErrors.password = t('auth.passwordRequired')
  } else if (password.value.length < 8) {
    fieldErrors.password = t('auth.passwordTooShort')
  }

  if (!passwordConfirmation.value) {
    fieldErrors.passwordConfirmation = t('auth.confirmPasswordRequired')
  } else if (password.value && password.value !== passwordConfirmation.value) {
    fieldErrors.passwordConfirmation = t('auth.passwordsMismatch')
  }

  return Object.keys(fieldErrors).length === 0
}

watch(password, () => delete fieldErrors.password)
watch(passwordConfirmation, () => delete fieldErrors.passwordConfirmation)

const mapServerErrors = (errors) => {
  const fieldI18nMap = {
    'Reset password token': { field: 'general', key: 'auth.resetTokenInvalid' },
    'Password confirmation': {
      field: 'passwordConfirmation',
      key: 'auth.passwordConfirmationMismatch',
    },
    Password: { field: 'password', key: 'auth.passwordTooShortServer' },
  }

  for (const message of errors) {
    let mapped = false
    for (const [prefix, mapping] of Object.entries(fieldI18nMap)) {
      if (message.startsWith(prefix)) {
        if (mapping.field === 'general') {
          error.value = t(mapping.key)
        } else {
          fieldErrors[mapping.field] = t(mapping.key)
        }
        mapped = true
        break
      }
    }
    if (!mapped) {
      error.value = t('common.somethingWentWrong')
    }
  }
}

const handleSubmit = async () => {
  error.value = null
  if (!validate()) return

  loading.value = true

  try {
    await apiClient.put('/auth/password', {
      reset_password_token: route.query.reset_password_token,
      password: password.value,
      password_confirmation: passwordConfirmation.value,
    })
    successMessage.value = t('auth.passwordResetSuccess')
    success.value = true
  } catch (error_) {
    const data = error_.response?.data
    if (data?.errors && Array.isArray(data.errors)) {
      for (const key of Object.keys(fieldErrors)) delete fieldErrors[key]
      mapServerErrors(data.errors)
    } else {
      error.value = t('common.somethingWentWrong')
    }
  } finally {
    loading.value = false
  }
}
</script>
